import React, { useEffect, useState, useRef } from 'react';
import styles from './Timer.module.css';

// Debug flag
const DEBUG = false; // Set to true for verbose console logging
const log = (...args: any[]) => DEBUG && console.log('[Timer Debug]', ...args);

export interface TimerProps {
  /** Time duration in seconds */
  duration: number;
  /** Callback when timer reaches zero */
  onComplete: () => void;
  /** Callback on each tick with remaining time */
  onTick?: (timeLeft: number) => void;
  /** Whether the timer is active */
  active?: boolean;
}

/**
 * Timer - Visual countdown timer
 * 
 * Displays a countdown timer with visual indicator and triggers callback on completion.
 */
const Timer: React.FC<TimerProps> = ({
  duration,
  onComplete,
  onTick,
  active = true,
}) => {
  // Store timeLeft in state
  const [timeLeft, setTimeLeft] = useState(duration);
  // Store initial duration in a ref to avoid unnecessary re-renders
  const initialDurationRef = useRef(duration);
  // Prevent multiple onComplete calls
  const hasCompletedRef = useRef(false);
  
  log('Timer rendered with props:', { duration, active, timeLeft });
  
  // Reset timer when duration prop changes
  useEffect(() => {
    log('Duration useEffect triggered', { duration, initialDuration: initialDurationRef.current });
    
    // Only update if duration actually changed
    if (initialDurationRef.current !== duration) {
      initialDurationRef.current = duration;
      setTimeLeft(duration);
      log('Timer reset due to duration change', { newDuration: duration });
      
      // Reset completion state when duration changes
      hasCompletedRef.current = false;
    }
  }, [duration]);

  useEffect(() => {
    log('Timer effect triggered', { active, duration });
    
    // Immediate check for zero or negative duration
    if (duration <= 0) {
      log('WARNING: Zero or negative duration detected', { duration });
      return;
    }
    
    if (!active) {
      log('Timer not active, skipping interval setup');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        log('Timer tick', { prev, next: prev - 1 });
        
        if (prev <= 1) {
          clearInterval(timer);
          
          // Only call onComplete once
          if (!hasCompletedRef.current) {
            log('Timer reached zero, calling onComplete');
            hasCompletedRef.current = true;
            onComplete();
          } else {
            log('Timer reached zero, but onComplete already called');
          }
          
          return 0;
        }
        
        // Call the onTick callback if provided
        if (onTick) {
          onTick(prev - 1);
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup on unmount or when dependencies change
    return () => {
      log('Clearing timer interval');
      clearInterval(timer);
    };
  }, [active, onComplete, onTick, duration]);

  // Calculate display values
  const minutes = Math.floor(timeLeft / 60);
  const remainingSeconds = timeLeft % 60;
  const timeString = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  // Calculate progress percentage
  const progressPercentage = `${(timeLeft / Math.max(1, duration)) * 100}%`;

  return (
    <div className={styles.timer} role="timer">
      <div className={styles.timeDisplay}>{timeString}</div>
      <div 
        className={styles.progressBar}
        style={{ 
          '--progress': progressPercentage 
        } as React.CSSProperties}
      />
      
      {/* Debug display in development */}
      {process.env.NODE_ENV === 'development' && DEBUG && (
        <div style={{ fontSize: '9px', color: '#999' }}>
          d:{duration}s t:{timeLeft}s a:{String(active)} c:{String(hasCompletedRef.current)}
        </div>
      )}
    </div>
  );
};

export default Timer; 